/**
 * Admin Center Page - Main dashboard with summary cards and quick actions
 * Epic 61: Admin Kozpont Dashboard (Stories 61-1, 61-2)
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { useAdminSummary } from '@/hooks/use-admin-summary';
import { cn, formatHuf } from '@/lib/utils';
import type { ZReport } from '@/types/pos.types';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DailyCloseWizard } from './DailyCloseWizard';
import { DailyOpenWizard } from './DailyOpenWizard';
import { QuickActionsPanel } from './QuickActionsPanel';

// ============================================
// SUMMARY CARD COMPONENT
// ============================================

interface SummaryCardProps {
  icon: React.ReactNode;
  title: string;
  value: string;
  subtitle: string;
  colorClass: string;
  onClick?: () => void;
}

function SummaryCard({ icon, title, value, subtitle, colorClass, onClick }: SummaryCardProps) {
  return (
    <Card
      className={cn(
        'cursor-pointer transition-all hover:shadow-lg hover:scale-[1.02]',
        onClick && 'hover:ring-2 hover:ring-primary/20'
      )}
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">
          {title}
        </CardTitle>
        <div className={cn('rounded-lg p-2', colorClass)}>{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold text-gray-900 dark:text-white">{value}</div>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
      </CardContent>
    </Card>
  );
}

// ============================================
// LOADING SKELETON
// ============================================

function SummaryCardSkeleton() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="h-4 w-24 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        <div className="h-10 w-10 animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700" />
      </CardHeader>
      <CardContent>
        <div className="h-9 w-20 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
        <div className="mt-2 h-4 w-32 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
      </CardContent>
    </Card>
  );
}

// ============================================
// MAIN PAGE
// ============================================

export function AdminCenterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { summary, isLoading, error, refetch } = useAdminSummary();
  const [showDailyOpen, setShowDailyOpen] = useState(false);
  const [showDailyClose, setShowDailyClose] = useState(false);

  return (
    <div className="min-h-full p-6 kgc-bg">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('admin.title')}</h1>
        <p className="text-gray-600 dark:text-gray-400">{t('admin.subtitle')}</p>
      </div>

      {/* Error state */}
      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <div className="flex items-center justify-between">
            <p className="text-sm text-red-600 dark:text-red-400">
              {t('common.error')}: {error}
            </p>
            <button
              onClick={() => void refetch()}
              className="text-sm font-medium text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
            >
              {t('common.retry')}
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards Grid */}
      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        {isLoading ? (
          <>
            <SummaryCardSkeleton />
            <SummaryCardSkeleton />
            <SummaryCardSkeleton />
            <SummaryCardSkeleton />
            <SummaryCardSkeleton />
          </>
        ) : (
          <>
            {/* 1. Lejart Berlesek */}
            <SummaryCard
              icon={
                <svg
                  className="h-5 w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
              title={t('admin.expiredRentals')}
              value={String(summary?.overdueRentals.count ?? 0)}
              subtitle={
                summary?.overdueRentals.count
                  ? t('admin.oldestDays', { days: summary.overdueRentals.oldestDays })
                  : t('admin.noExpiredRentals')
              }
              colorClass={(summary?.overdueRentals.count ?? 0) > 0 ? 'bg-red-500' : 'bg-green-500'}
              onClick={() => navigate('/rental?status=OVERDUE')}
            />

            {/* 2. Kintlevosegek */}
            <SummaryCard
              icon={
                <svg
                  className="h-5 w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              }
              title={t('admin.receivables')}
              value={formatHuf(summary?.receivables.totalAmount ?? 0)}
              subtitle={
                (summary?.receivables.totalAmount ?? 0) > 0
                  ? t('admin.overdue30Partners', {
                      count: summary?.receivables.over30DaysPartnerCount ?? 0,
                    })
                  : t('admin.noReceivables')
              }
              colorClass={
                (summary?.receivables.totalAmount ?? 0) > 0 ? 'bg-amber-500' : 'bg-green-500'
              }
              onClick={() => navigate('/invoices?filter=overdue')}
            />

            {/* 3. Szerviz Munkalapok */}
            <SummaryCard
              icon={
                <svg
                  className="h-5 w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
              }
              title={t('admin.serviceWorksheets')}
              value={String(
                (summary?.serviceWorksheets.pending ?? 0) +
                  (summary?.serviceWorksheets.inProgress ?? 0)
              )}
              subtitle={t('admin.worksheetStats', {
                waiting: summary?.serviceWorksheets.pending ?? 0,
                inProgress: summary?.serviceWorksheets.inProgress ?? 0,
                completed: summary?.serviceWorksheets.completed ?? 0,
              })}
              colorClass="bg-blue-500"
              onClick={() => navigate('/worksheet')}
            />

            {/* 4. Mai Foglalasok */}
            <SummaryCard
              icon={
                <svg
                  className="h-5 w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              }
              title={t('admin.todayBookings')}
              value={String(summary?.todayBookings.count ?? 0)}
              subtitle={t('admin.todayPlanned')}
              colorClass="bg-green-500"
              onClick={() => navigate('/rental')}
            />

            {/* 5. Feladatok */}
            <SummaryCard
              icon={
                <svg
                  className="h-5 w-5 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                  />
                </svg>
              }
              title={t('admin.tasks')}
              value={String(summary?.tasks.assigned ?? 0)}
              subtitle={
                (summary?.tasks.overdue ?? 0) > 0
                  ? t('admin.expiredTasks', { count: summary?.tasks.overdue ?? 0 })
                  : t('admin.noExpiredTasks')
              }
              colorClass={(summary?.tasks.overdue ?? 0) > 0 ? 'bg-orange-500' : 'bg-primary'}
              onClick={() => navigate('/tasks')}
            />
          </>
        )}
      </div>

      {/* Quick Actions Panel */}
      <QuickActionsPanel
        onDailyOpen={() => setShowDailyOpen(true)}
        onDailyClose={() => setShowDailyClose(true)}
      />

      {/* Daily Open Wizard Modal */}
      {showDailyOpen && (
        <DailyOpenWizard
          summary={summary}
          onClose={() => setShowDailyOpen(false)}
          onComplete={() => {
            setShowDailyOpen(false);
            void refetch();
            // Redirect to POS page after opening
            navigate('/sales/pos');
          }}
        />
      )}

      {/* Daily Close Wizard Modal */}
      {showDailyClose && (
        <DailyCloseWizard
          summary={summary}
          onClose={() => setShowDailyClose(false)}
          onComplete={(zReport?: ZReport) => {
            setShowDailyClose(false);
            void refetch();
            if (zReport) {
              // Z-report is available, show it by navigating to finance page
              // The Z-report data is captured in POS session hooks cache
              navigate('/finance/cash');
            }
          }}
        />
      )}
    </div>
  );
}
